import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db, auth } from '../services/firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { sendSubscriptionConfirmationEmail, isEmailConfigured } from '../services/emailService';

function CreateClub() {
  const [user] = useAuthState(auth);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [subdomainAvailable, setSubdomainAvailable] = useState(null);
  const [checkingSubdomain, setCheckingSubdomain] = useState(false);

  // Step 1: Club Information
  const [clubInfo, setClubInfo] = useState({
    name: '',
    subdomain: '',
    email: '',
    phone: '',
    location: '',
    primaryColor: '#1e3a5f',
    secondaryColor: '#ce1126'
  });

  // Step 2: Admin Account
  const [useCurrentAccount, setUseCurrentAccount] = useState(true);
  const [adminInfo, setAdminInfo] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  // Step 3: Payment status
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [createdClubId, setCreatedClubId] = useState('');
  const [adminEmail, setAdminEmail] = useState(''); // Track the admin email for success page

  const cities = [
    'Port of Spain', 'San Fernando', 'Chaguanas', 'Arima', 'Point Fortin',
    'Scarborough (Tobago)', 'Couva', 'Sangre Grande', 'Tunapuna', 'Marabella',
    'Diego Martin', 'San Juan', 'Fyzabad', 'Princes Town', 'Siparia',
    'Penal', 'Rio Claro', 'Mayaro', 'Toco', 'Other'
  ];

  // Auto-generate subdomain from club name
  useEffect(() => {
    if (clubInfo.name) {
      const subdomain = clubInfo.name
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
        .slice(0, 20);
      setClubInfo(prev => ({ ...prev, subdomain }));
    }
  }, [clubInfo.name]);

  // Check subdomain availability
  useEffect(() => {
    const checkSubdomain = async () => {
      if (!clubInfo.subdomain || clubInfo.subdomain.length < 3) {
        setSubdomainAvailable(null);
        return;
      }

      setCheckingSubdomain(true);
      try {
        const clubDoc = await getDoc(doc(db, 'clubs', clubInfo.subdomain));
        setSubdomainAvailable(!clubDoc.exists());
      } catch (err) {
        console.error('Error checking subdomain:', err);
      }
      setCheckingSubdomain(false);
    };

    const debounce = setTimeout(checkSubdomain, 500);
    return () => clearTimeout(debounce);
  }, [clubInfo.subdomain]);

  // If user is logged in, pre-fill admin email
  useEffect(() => {
    if (user) {
      setAdminInfo(prev => ({ ...prev, email: user.email }));
    }
  }, [user]);

  const validateStep1 = () => {
    if (!clubInfo.name || clubInfo.name.length < 3) {
      setError('Club name must be at least 3 characters');
      return false;
    }
    if (!clubInfo.subdomain || clubInfo.subdomain.length < 3) {
      setError('Subdomain must be at least 3 characters');
      return false;
    }
    if (!subdomainAvailable) {
      setError('This subdomain is not available');
      return false;
    }
    if (!clubInfo.email || !clubInfo.email.includes('@')) {
      setError('Please enter a valid club email');
      return false;
    }
    if (!clubInfo.phone) {
      setError('Please enter a club phone number');
      return false;
    }
    if (!clubInfo.location) {
      setError('Please select a location');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    // If using current account and logged in, no validation needed
    if (useCurrentAccount && user) {
      return true;
    }
    
    // Creating new admin account - validate all fields
    if (!adminInfo.name) {
      setError('Please enter the admin name');
      return false;
    }
    if (!adminInfo.email || !adminInfo.email.includes('@')) {
      setError('Please enter a valid email');
      return false;
    }
    if (!adminInfo.password || adminInfo.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    if (adminInfo.password !== adminInfo.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleNext = async () => {
    setError('');

    if (step === 1) {
      if (!validateStep1()) return;
      setStep(2);
    } else if (step === 2) {
      if (!validateStep2()) return;
      
      // If creating a new admin account (not using current)
      if (!useCurrentAccount || !user) {
        setLoading(true);
        try {
          // Sign out current user if any
          if (user) {
            await signOut(auth);
          }
          // Create new admin account
          await createUserWithEmailAndPassword(auth, adminInfo.email, adminInfo.password);
          // Auth state will update automatically
        } catch (err) {
          if (err.code === 'auth/email-already-in-use') {
            setError('An account with this email already exists. Please use a different email or log in with that account.');
          } else {
            setError(err.message);
          }
          setLoading(false);
          return;
        }
        setLoading(false);
      }
      
      setStep(3);
    }
  };

  const handleBack = () => {
    setError('');
    setStep(step - 1);
  };

  const handlePayment = async () => {
    setLoading(true);
    setError('');

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setError('Please log in to continue');
        setLoading(false);
        return;
      }

      // Create subscription dates
      const now = new Date();
      const periodEnd = new Date(now);
      periodEnd.setMonth(periodEnd.getMonth() + 1);

      // Determine admin name
      const finalAdminName = useCurrentAccount 
        ? (adminInfo.name || currentUser.email.split('@')[0])
        : adminInfo.name;

      // Step 1: Check if user already exists and has a club
      const existingUserDoc = await getDoc(doc(db, 'users', currentUser.uid));
      
      if (existingUserDoc.exists()) {
        const existingData = existingUserDoc.data();
        // If user already has a club and it's different, warn them
        if (existingData.clubId && existingData.clubId !== clubInfo.subdomain) {
          const confirmChange = window.confirm(
            `You are currently associated with another club (${existingData.clubId}). ` +
            `Creating this club will make you the admin of "${clubInfo.name}" instead. ` +
            `Continue?`
          );
          if (!confirmChange) {
            setLoading(false);
            return;
          }
        }
      }

      // Step 2: Create/update user document as club_admin
      console.log('Creating user document...');
      await setDoc(doc(db, 'users', currentUser.uid), {
        email: currentUser.email,
        name: finalAdminName,
        role: 'club_admin',
        clubId: clubInfo.subdomain,
        createdAt: existingUserDoc.exists() ? existingUserDoc.data().createdAt : now,
        updatedAt: now
      });
      console.log('User document created successfully');

      // Small delay to ensure user doc is available for rules
      await new Promise(resolve => setTimeout(resolve, 500));

      // Step 2: Create the club document
      const clubData = {
        name: clubInfo.name,
        subdomain: clubInfo.subdomain,
        email: clubInfo.email,
        phone: clubInfo.phone,
        location: clubInfo.location,
        address: clubInfo.location + ', Trinidad & Tobago',
        primaryColor: clubInfo.primaryColor,
        secondaryColor: clubInfo.secondaryColor,
        status: 'active',
        createdAt: now,
        createdBy: currentUser.uid,
        subscription: {
          status: 'active',
          startDate: now,
          currentPeriodEnd: periodEnd,
          lastPaymentDate: now,
          lastPaymentAmount: 500,
          nextPaymentDue: periodEnd
        }
      };

      console.log('Creating club document...');
      await setDoc(doc(db, 'clubs', clubInfo.subdomain), clubData);
      console.log('Club created successfully');

      // Step 3: Create default homepage content
      try {
        console.log('Creating homepage content...');
        await setDoc(doc(db, 'content', `${clubInfo.subdomain}_homepage`), {
          clubId: clubInfo.subdomain,
          heroTitle: clubInfo.name.toUpperCase(),
          heroSubtitle: 'Welcome to our club',
          updatedAt: now
        });
        console.log('Homepage content created successfully');
      } catch (contentErr) {
        // Non-critical error - club is still created, admin can set up content later
        console.warn('Could not create default homepage content:', contentErr);
      }

      // Step 4: Send confirmation email (non-critical)
      if (isEmailConfigured()) {
        try {
          await sendSubscriptionConfirmationEmail(
            clubInfo.email,
            clubInfo.name,
            500,
            periodEnd.toLocaleDateString()
          );
        } catch (emailErr) {
          console.error('Failed to send confirmation email:', emailErr);
        }
      }

      // Success!
      setAdminEmail(currentUser.email);
      setCreatedClubId(clubInfo.subdomain);
      setPaymentComplete(true);
      setStep(4);

    } catch (err) {
      console.error('Error creating club:', err);
      setError('Failed to create club: ' + err.message);
    }
    
    setLoading(false);
  };

  // Simulate WiPay payment (in production, redirect to WiPay)
  const initiatePayment = () => {
    // For demo, we'll simulate successful payment
    // In production:
    // 1. Create pending order/subscription record
    // 2. Redirect to WiPay with callback URL
    // 3. Handle callback to complete registration
    handlePayment();
  };

  return (
    <div className="create-club-page">
      <header className="create-club-header">
        <Link to="/" className="back-link">← Back to ClubLink TT</Link>
        <h1>Register Your Club</h1>
      </header>

      {/* Progress Steps */}
      <div className="progress-steps">
        <div className={`progress-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
          <span className="step-number">1</span>
          <span className="step-label">Club Info</span>
        </div>
        <div className={`progress-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
          <span className="step-number">2</span>
          <span className="step-label">Admin Account</span>
        </div>
        <div className={`progress-step ${step >= 3 ? 'active' : ''} ${step > 3 ? 'completed' : ''}`}>
          <span className="step-number">3</span>
          <span className="step-label">Payment</span>
        </div>
        <div className={`progress-step ${step >= 4 ? 'active' : ''}`}>
          <span className="step-number">4</span>
          <span className="step-label">Complete</span>
        </div>
      </div>

      <div className="create-club-content">
        {error && <div className="error-message">{error}</div>}

        {/* Step 1: Club Information */}
        {step === 1 && (
          <div className="form-step">
            <h2>Club Information</h2>
            <p className="step-description">Tell us about your football club</p>

            <div className="form-group">
              <label>Club Name *</label>
              <input
                type="text"
                value={clubInfo.name}
                onChange={(e) => setClubInfo({ ...clubInfo, name: e.target.value })}
                placeholder="e.g., Pro Series FC"
              />
            </div>

            <div className="form-group">
              <label>Club URL (Subdomain) *</label>
              <div className="subdomain-input">
                <span className="subdomain-prefix">clublinktt.com/</span>
                <input
                  type="text"
                  value={clubInfo.subdomain}
                  onChange={(e) => setClubInfo({ ...clubInfo, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '') })}
                  placeholder="proseriesfc"
                />
              </div>
              {checkingSubdomain && <span className="subdomain-checking">Checking availability...</span>}
              {!checkingSubdomain && subdomainAvailable === true && (
                <span className="subdomain-available">Available!</span>
              )}
              {!checkingSubdomain && subdomainAvailable === false && (
                <span className="subdomain-taken">This subdomain is taken</span>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Club Email *</label>
                <input
                  type="email"
                  value={clubInfo.email}
                  onChange={(e) => setClubInfo({ ...clubInfo, email: e.target.value })}
                  placeholder="info@yourclub.com"
                />
              </div>

              <div className="form-group">
                <label>Club Phone *</label>
                <input
                  type="tel"
                  value={clubInfo.phone}
                  onChange={(e) => setClubInfo({ ...clubInfo, phone: e.target.value })}
                  placeholder="(868) 123-4567"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Location *</label>
              <select
                value={clubInfo.location}
                onChange={(e) => setClubInfo({ ...clubInfo, location: e.target.value })}
              >
                <option value="">Select a city</option>
                {cities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Primary Color</label>
                <div className="color-picker">
                  <input
                    type="color"
                    value={clubInfo.primaryColor}
                    onChange={(e) => setClubInfo({ ...clubInfo, primaryColor: e.target.value })}
                  />
                  <span>{clubInfo.primaryColor}</span>
                </div>
              </div>

              <div className="form-group">
                <label>Secondary Color</label>
                <div className="color-picker">
                  <input
                    type="color"
                    value={clubInfo.secondaryColor}
                    onChange={(e) => setClubInfo({ ...clubInfo, secondaryColor: e.target.value })}
                  />
                  <span>{clubInfo.secondaryColor}</span>
                </div>
              </div>
            </div>

            {/* Club Preview */}
            <div className="club-preview">
              <h3>Preview</h3>
              <div className="preview-card" style={{ 
                '--preview-primary': clubInfo.primaryColor,
                '--preview-secondary': clubInfo.secondaryColor 
              }}>
                <div className="preview-logo" style={{ background: clubInfo.primaryColor }}>
                  {clubInfo.name ? clubInfo.name.charAt(0).toUpperCase() : '?'}
                </div>
                <div className="preview-info">
                  <h4>{clubInfo.name || 'Your Club Name'}</h4>
                  <p>{clubInfo.location || 'Location'}</p>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button onClick={handleNext} className="btn-primary" disabled={loading}>
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Admin Account */}
        {step === 2 && (
          <div className="form-step">
            <h2>Admin Account</h2>
            <p className="step-description">
              Set up the administrator account for your club
            </p>

            {user && (
              <div className="admin-account-options">
                <label className="account-option">
                  <input
                    type="radio"
                    name="accountType"
                    checked={useCurrentAccount}
                    onChange={() => setUseCurrentAccount(true)}
                  />
                  <div className="option-content">
                    <span className="option-title">Use my current account</span>
                    <span className="option-email">{user.email}</span>
                  </div>
                </label>
                
                <label className="account-option">
                  <input
                    type="radio"
                    name="accountType"
                    checked={!useCurrentAccount}
                    onChange={() => setUseCurrentAccount(false)}
                  />
                  <div className="option-content">
                    <span className="option-title">Create a different admin account</span>
                    <span className="option-desc">Set up a separate account for the club admin</span>
                  </div>
                </label>
              </div>
            )}

            {/* Show form if not logged in OR if creating different account */}
            {(!user || !useCurrentAccount) && (
              <div className="admin-form-fields">
                <div className="form-group">
                  <label>Admin Full Name *</label>
                  <input
                    type="text"
                    value={adminInfo.name}
                    onChange={(e) => setAdminInfo({ ...adminInfo, name: e.target.value })}
                    placeholder="Enter admin's full name"
                  />
                </div>

                <div className="form-group">
                  <label>Admin Email *</label>
                  <input
                    type="email"
                    value={adminInfo.email}
                    onChange={(e) => setAdminInfo({ ...adminInfo, email: e.target.value })}
                    placeholder="Enter admin's email"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Password *</label>
                    <input
                      type="password"
                      value={adminInfo.password}
                      onChange={(e) => setAdminInfo({ ...adminInfo, password: e.target.value })}
                      placeholder="At least 6 characters"
                    />
                  </div>

                  <div className="form-group">
                    <label>Confirm Password *</label>
                    <input
                      type="password"
                      value={adminInfo.confirmPassword}
                      onChange={(e) => setAdminInfo({ ...adminInfo, confirmPassword: e.target.value })}
                      placeholder="Confirm password"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Show name field even when using current account */}
            {user && useCurrentAccount && (
              <div className="admin-form-fields">
                <div className="form-group">
                  <label>Your Name (for admin profile)</label>
                  <input
                    type="text"
                    value={adminInfo.name}
                    onChange={(e) => setAdminInfo({ ...adminInfo, name: e.target.value })}
                    placeholder="Enter your full name"
                  />
                </div>
              </div>
            )}

            <div className="form-actions">
              <button onClick={handleBack} className="btn-secondary">
                Back
              </button>
              <button onClick={handleNext} className="btn-primary" disabled={loading}>
                {loading ? 'Creating account...' : 'Continue to Payment'}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Payment */}
        {step === 3 && (
          <div className="form-step">
            <h2>Complete Payment</h2>
            <p className="step-description">Subscribe to ClubLink TT</p>

            <div className="payment-summary">
              <div className="summary-header">
                <h3>Order Summary</h3>
              </div>
              
              <div className="summary-item">
                <span>ClubLink TT Monthly Subscription</span>
                <span>TT$ 500.00</span>
              </div>
              
              <div className="summary-details">
                <p><strong>Club:</strong> {clubInfo.name}</p>
                <p><strong>URL:</strong> clublinktt.com/?club={clubInfo.subdomain}</p>
                <p><strong>Admin:</strong> {user?.email || adminInfo.email}</p>
              </div>

              <div className="summary-total">
                <span>Total Due Today</span>
                <span>TT$ 500.00</span>
              </div>

              <div className="subscription-info">
                <p>Your subscription will renew automatically each month at TT$ 500.</p>
                <p>You can cancel anytime from your admin dashboard.</p>
              </div>
            </div>

            <div className="payment-methods">
              <h3>Payment Method</h3>
              <div className="payment-option selected">
                <span className="payment-icon">💳</span>
                <div>
                  <p className="payment-name">WiPay</p>
                  <p className="payment-desc">Credit/Debit Card</p>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button onClick={handleBack} className="btn-secondary" disabled={loading}>
                Back
              </button>
              <button onClick={initiatePayment} className="btn-primary btn-pay" disabled={loading}>
                {loading ? 'Processing...' : 'Pay TT$ 500'}
              </button>
            </div>

            <p className="payment-secure">
              🔒 Secure payment processed by WiPay
            </p>
          </div>
        )}

        {/* Step 4: Complete */}
        {step === 4 && paymentComplete && (
          <div className="form-step completion-step">
            <div className="success-icon">✓</div>
            <h2>Welcome to ClubLink TT!</h2>
            <p className="step-description">Your club has been successfully created</p>

            <div className="completion-details">
              <div className="detail-item">
                <span className="detail-label">Club Name</span>
                <span className="detail-value">{clubInfo.name}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Your Club URL</span>
                <span className="detail-value">
                  <a href={`/?club=${createdClubId}`} target="_blank" rel="noopener noreferrer">
                    clublinktt.com/?club={createdClubId}
                  </a>
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Admin Account</span>
                <span className="detail-value">{adminEmail}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Subscription</span>
                <span className="detail-value">Active - Next payment in 30 days</span>
              </div>
            </div>

            <div className="next-steps">
              <h3>Next Steps</h3>
              <ul>
                <li>Set up your team rosters</li>
                <li>Add your players</li>
                <li>Create your schedule</li>
                <li>Customize your club page</li>
              </ul>
            </div>

            <div className="form-actions">
              <a href={`/?club=${createdClubId}`} className="btn-secondary">
                View Club Site
              </a>
              <a href={`/admin/dashboard?club=${createdClubId}`} className="btn-primary">
                Go to Admin Dashboard
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CreateClub;
